import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, ArrowRight, Star, Languages, ShieldCheck, Receipt } from "lucide-react";
import { brand, services, inspection, remodeling, painting, roofline } from "../data/site";
import { legalDocs } from "../data/legal";
import { getArea } from "../data/serviceAreas";

const featuredAreas = ["phoenix", "peoria", "glendale", "surprise", "sun-city", "goodyear", "buckeye", "scottsdale", "mesa", "chandler", "gilbert", "tempe"].map(getArea);

// lucide-react dropped brand marks, so TikTok is drawn here. currentColor so
// it inherits the same hover treatment as the Facebook and Instagram icons,
// and aria-hidden because the accessible name lives on the wrapping link.
const TikTok = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M16.5 2h-2.9v12.6a2.3 2.3 0 1 1-2.3-2.3c.2 0 .4 0 .6.1V9.4a5.6 5.6 0 0 0-.6 0 5.3 5.3 0 1 0 5.3 5.3V8.6a6.5 6.5 0 0 0 3.8 1.2V6.9a3.7 3.7 0 0 1-3.9-3.6V2Z" />
  </svg>
);

const Footer = () => {
  return (
    <footer lang="en" className="section-dark text-white pb-20 md:pb-0/70">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img loading="lazy" decoding="async" src={brand.logo} alt={brand.name} width="820" height="814" className="h-28 w-auto object-contain mb-5 -ml-1" />
            <p className="text-sm leading-relaxed mb-5">
              Residential roof repair, replacement and inspections across Phoenix and Maricopa County — tile, shingle, flat and foam roofs.
            </p>
            {/* Each icon appears only once that profile is set in src/data/site.js */}
            <div className="flex items-center gap-3">
              {brand.facebook && (
                <a
                  href={brand.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${brand.shortName} on Facebook`}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 hover:border-brand hover:text-brand transition-colors"
                >
                  <Facebook size={18} />
                </a>
              )}
              {brand.instagram && (
                <a
                  href={brand.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${brand.shortName} on Instagram`}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 hover:border-brand hover:text-brand transition-colors"
                >
                  <Instagram size={18} />
                </a>
              )}
              {brand.tiktok && (
                <a
                  href={brand.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${brand.shortName} on TikTok`}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/20 hover:border-brand hover:text-brand transition-colors"
                >
                  <TikTok size={18} />
                </a>
              )}
              {brand.yelp && (
                <a
                  href={brand.yelp}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${brand.shortName} on Yelp`}
                  className="inline-flex items-center justify-center h-10 px-3 rounded-full border border-white/20 hover:border-brand hover:text-brand text-xs font-semibold transition-colors"
                >
                  Yelp
                </a>
              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-heading text-lg tracking-wide mb-5 uppercase">Services</h3>
            <ul className="space-y-3 text-sm">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="hover:text-brand transition-colors flex items-center gap-2 group">
                    <ArrowRight size={14} className="text-brand opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {s.name}
                  </Link>
                </li>
              ))}
              {remodeling.enabled && (
                <li>
                  <Link to="/remodeling" className="hover:text-brand transition-colors flex items-center gap-2 group">
                    <ArrowRight size={14} className="text-brand opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Home Remodeling
                  </Link>
                </li>
              )}
              {roofline.enabled && (
                <li>
                  <Link to="/attic-roofline" className="hover:text-brand transition-colors flex items-center gap-2 group">
                    <ArrowRight size={14} className="text-brand opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Attic &amp; Roofline
                  </Link>
                </li>
              )}
              {painting.enabled && (
                <li>
                  <Link to="/painting" className="hover:text-brand transition-colors flex items-center gap-2 group">
                    <ArrowRight size={14} className="text-brand opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Interior &amp; Exterior Painting
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-heading text-lg tracking-wide mb-5 uppercase">Get In Touch</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-brand shrink-0 mt-0.5" />
                <a href={`tel:${brand.phoneRaw}`} className="hover:text-brand transition-colors">{brand.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-brand shrink-0 mt-0.5" />
                <a href={`mailto:${brand.emailRaw || brand.email}`} className="hover:text-brand transition-colors">{brand.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-brand shrink-0 mt-0.5" />
                <span>
                  Serving Phoenix &amp; Maricopa County
                  {brand.address && <><br /><span className="text-white/50">{brand.address}</span></>}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-brand shrink-0 mt-0.5" />
                <span>
                  Inspections: {inspection.hours}
                  {inspection.daylight && (
                    <><br /><span className="text-white/60 text-xs">{inspection.daylight}</span></>
                  )}
                  {brand.hours && <><br /><span className="text-white/60">{brand.hoursLabel}: {brand.hours}</span></>}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-brand shrink-0 mt-0.5" />
                <Link to="/insurance-claims" className="hover:text-brand transition-colors">Storm & Insurance Help</Link>
              </li>
              <li>
                <Link to="/lifecycle-plan" className="hover:text-brand transition-colors">Lifecycle Plan</Link>
              </li>
              <li className="flex items-start gap-3">
                <Receipt size={18} className="text-brand shrink-0 mt-0.5" />
                <Link to="/roof-replacement-cost" className="hover:text-brand transition-colors">What A Roof Costs</Link>
              </li>
              <li>
                <Link to="/resources" className="hover:text-brand transition-colors">Roofing Resources &amp; Guides</Link>
              </li>
              <li className="flex items-start gap-3">
                <Languages size={18} className="text-brand shrink-0 mt-0.5" />
                <Link to="/espanol" className="hover:text-brand transition-colors">Español · Hablamos Español</Link>
              </li>
              {brand.googleWriteReviewUrl && (
                <li className="flex items-start gap-3">
                  <Star size={18} className="text-brand shrink-0 mt-0.5" />
                  <a href={brand.googleWriteReviewUrl} target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">
                    Leave a Google Review
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Service areas */}
          <div>
            <h3 className="text-white font-heading text-lg tracking-wide mb-5 uppercase"><Link to="/service-areas" className="hover:text-brand transition-colors">Service Areas</Link></h3>
            <div className="flex flex-wrap gap-2">
              {featuredAreas.map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-xs border border-white/15 rounded-full px-3 py-1 hover:border-brand hover:text-brand transition-colors">
                  {a.name}
                </Link>
              ))}
              <Link to="/service-areas" className="text-xs border border-brand/60 text-brand rounded-full px-3 py-1 hover:bg-brand hover:text-ink transition-colors">
                All Maricopa County →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} {brand.legalName}. All rights reserved.</p>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-1">
            {legalDocs.map((d) => (
              <Link key={d.slug} to={`/${d.slug}`} className="hover:text-brand transition-colors">{d.title}</Link>
            ))}
          </nav>
          {brand.license && <p className="text-white/40">{brand.license}</p>}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
